import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour,
  CalendarCheck,
  CurrencyCircleDollar,
  EnvelopeSimple,
  ChartLineUp,
  ChatCircleDots,
  SignOut,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const nav = [
  { to: "/app", label: "Overview", icon: SquaresFour, end: true, testid: "nav-overview" },
  { to: "/app/renewals", label: "Lease Renewals", icon: CalendarCheck, testid: "nav-renewals" },
  { to: "/app/past-due", label: "Past-Due", icon: CurrencyCircleDollar, testid: "nav-past-due" },
  { to: "/app/triage", label: "Email Triage", icon: EnvelopeSimple, testid: "nav-triage" },
  { to: "/app/market", label: "Market Trends", icon: ChartLineUp, testid: "nav-market" },
  { to: "/app/assistant", label: "Assistant", icon: ChatCircleDots, testid: "nav-assistant" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.full_name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-sand-100">
      <aside className="hidden lg:flex w-64 flex-col border-r border-stone-200 bg-sand-50/80 backdrop-blur-sm">
        <div className="px-5 py-6 border-b border-stone-200">
          <button onClick={() => navigate("/")} data-testid="sidebar-logo">
            <Logo />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-forest-50 text-forest-600 font-medium"
                    : "text-stone-600 hover:bg-stone-100"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-stone-100 transition-colors"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src="https://images.unsplash.com/photo-1590735627513-59a186ed0984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3NzY5Nzk3MDN8MA&ixlib=rb-4.1.0&q=85" />
                  <AvatarFallback className="bg-forest-500 text-white text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-stone-900 truncate">{user?.full_name}</div>
                  <div className="text-xs text-stone-500 truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-button">
                <SignOut size={16} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        {/* mobile topbar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-sand-50">
          <Logo />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="h-8 w-8 cursor-pointer" data-testid="mobile-user-menu">
                <AvatarFallback className="bg-forest-500 text-white text-xs">{initials}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {nav.map((n) => (
                <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={`mobile-${n.testid}`}>
                  <n.icon size={16} className="mr-2" /> {n.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="mobile-logout-button">
                <SignOut size={16} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
